import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Mail, Lock, User, Gift } from 'lucide-react';
import { useAuth } from '@hooks/useAuth';
import Button from '@components/common/Button';
import Input from '@components/common/Input';
import Modal from '@components/common/Modal';
import Terms from '@pages/public/Terms';
import Privacy from '@pages/public/Privacy';

const registerSchema = z.object({
  name: z.string().min(3, 'Nome deve ter no mínimo 3 caracteres'),
  email: z.string().email('Email inválido'),
  password: z.string().min(6, 'Senha deve ter no mínimo 6 caracteres'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Senhas não conferem',
  path: ['confirmPassword'],
});

const Register = () => {
  const { register: registerUser } = useAuth();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || '';
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await registerUser({
        name: data.name,
        email: data.email,
        password: data.password,
        referralCode: refCode || undefined,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {refCode && (
        <div className="mb-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-3 flex items-center gap-2">
          <Gift size={16} className="text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
          <p className="text-sm text-emerald-700 dark:text-emerald-300">
            Voce foi convidado! Codigo: <strong>{refCode}</strong>
          </p>
        </div>
      )}
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Criar sua conta
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-8">
        Comece a controlar suas finanças gratuitamente
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Nome completo"
          type="text"
          placeholder="Seu nome"
          leftIcon={<User size={18} />}
          error={errors.name?.message}
          {...register('name')}
        />

        <Input
          label="Email"
          type="email"
          placeholder="seu@email.com"
          leftIcon={<Mail size={18} />}
          error={errors.email?.message}
          {...register('email')}
        />

        <Input
          label="Senha"
          type={showPassword ? 'text' : 'password'}
          placeholder="••••••••"
          leftIcon={<Lock size={18} />}
          rightIcon={
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="focus:outline-none"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          }
          error={errors.password?.message}
          {...register('password')}
        />

        <Input
          label="Confirmar senha"
          type={showPassword ? 'text' : 'password'}
          placeholder="••••••••"
          leftIcon={<Lock size={18} />}
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />

        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            required
            className="w-4 h-4 mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Li e aceito os{' '}
            <button
              type="button"
              onClick={() => setTermsOpen(true)}
              className="text-primary-600 hover:text-primary-700 underline"
            >
              Termos de Uso
            </button>{' '}
            e{' '}
            <button
              type="button"
              onClick={() => setPrivacyOpen(true)}
              className="text-primary-600 hover:text-primary-700 underline"
            >
              Política de Privacidade
            </button>
          </span>
        </div>

        <Button type="submit" fullWidth isLoading={isLoading}>
          Criar conta
        </Button>
      </form>


      <p className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400">
        Já tem uma conta?{' '}
        <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
          Fazer login
        </Link>
      </p>

      <Modal
        isOpen={termsOpen}
        onClose={() => setTermsOpen(false)}
        title="Termos de Uso"
        size="xl"
      >
        <div className="max-h-[60vh] overflow-y-auto pr-1">
          <Terms embedded />
        </div>
      </Modal>

      <Modal
        isOpen={privacyOpen}
        onClose={() => setPrivacyOpen(false)}
        title="Política de Privacidade"
        size="xl"
      >
        <div className="max-h-[60vh] overflow-y-auto pr-1">
          <Privacy embedded />
        </div>
      </Modal>
    </div>
  );
};

export default Register;
